<template>
  <div class="container-fluid tuscfdis-ai-page">
    <div class="row">
      <div class="col-12" style="margin-top: 20px;">
        <div class="card ai-chat-card">
          <div class="card-header ai-chat-header">
            <div>
              <h5 class="mb-0">TusCFDIs IA</h5>
              <small class="text-muted">Agente IA que te ayuda con tus consultas sobre CFDIs</small>
            </div>
          </div>

          <div class="card-body ai-chat-body">
            <div class="ai-chat-messages" ref="messagesContainer">
              <div
                v-for="message in messages"
                :key="message.id"
                class="ai-message-row"
                :class="`ai-message-row-${message.role}`"
              >
                <div class="ai-message-bubble" :class="`ai-message-bubble-${message.role}`">
                  <div class="ai-message-role">{{ message.role === 'user' ? 'Tú' : 'TusCFDIs IA' }}</div>
                  <div class="ai-message-text" v-html="renderMessageContent(message.content)"></div>
                  <div class="ai-message-time">{{ message.time }}</div>
                </div>
              </div>

              <div v-if="isSending" class="ai-message-row ai-message-row-assistant">
                <div class="ai-message-bubble ai-message-bubble-assistant">
                  <div class="ai-message-role">TusCFDIs IA</div>
                  <div class="ai-message-text">Escribiendo respuesta...</div>
                </div>
              </div>
            </div>

            <div v-if="errorMessage" class="alert alert-warning mt-3 mb-0" role="alert">
              {{ errorMessage }}
            </div>

            <form class="ai-chat-input" @submit.prevent="sendMessage">
              <textarea
                v-model="draftMessage"
                class="form-control"
                rows="3"
                maxlength="2000"
                placeholder="Escribe tu mensaje para probar el chat..."
                :disabled="isSending"
              ></textarea>
              <div class="ai-chat-actions">
                <small class="text-muted">{{ draftMessage.length }}/2000</small>
                <button type="submit" class="btn btn-primary" :disabled="isSendDisabled">
                  {{ isSending ? 'Enviando...' : 'Enviar' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import aiService from '@/services/ai';
import { getGlobalPeriod } from '@/services/globalPeriod';

export default {
  name: 'TuscfdisAiChat',
  data() {
    return {
      draftMessage: '',
      isSending: false,
      errorMessage: '',
      messageSequence: 1,
      messages: [
        {
          id: 0,
          role: 'assistant',
          content: 'Hola, soy tu agente personal y estoy aquí para ayudarte con las consultas sobre tus CFDIs.',
          time: new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }),
        },
      ],
    };
  },
  computed: {
    isSendDisabled() {
      return this.isSending || String(this.draftMessage || '').trim() === '';
    },
  },
  methods: {
    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    isSafeHttpUrl(url) {
      try {
        const parsed = new URL(String(url || '').trim());
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
      } catch (error) {
        return false;
      }
    },
    renderMessageContent(content) {
      const raw = String(content || '');

      // Escape first, then selectively inject safe links only.
      let html = this.escapeHtml(raw);

      html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/gi, (match, text, url) => {
        const normalizedUrl = String(url || '').trim();
        if (!this.isSafeHttpUrl(normalizedUrl)) {
          return match;
        }

        const safeText = this.escapeHtml(String(text || '').trim() || normalizedUrl);
        const safeHref = this.escapeHtml(normalizedUrl);

        return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${safeText}</a>`;
      });

      html = html.replace(/(^|\s)(https?:\/\/[^\s<]+)(?=$|\s)/gi, (fullMatch, prefix, url) => {
        const normalizedUrl = String(url || '').trim();
        if (!this.isSafeHttpUrl(normalizedUrl)) {
          return fullMatch;
        }

        const safeHref = this.escapeHtml(normalizedUrl);
        return `${prefix}<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${safeHref}</a>`;
      });

      return html.replace(/\n/g, '<br>');
    },
    nextMessageId() {
      const id = this.messageSequence;
      this.messageSequence += 1;
      return id;
    },
    getCurrentTime() {
      return new Date().toLocaleTimeString('es-MX', {
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    pushMessage(role, content) {
      this.messages.push({
        id: this.nextMessageId(),
        role,
        content: String(content || '').trim(),
        time: this.getCurrentTime(),
      });
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (!container) return;
      container.scrollTop = container.scrollHeight;
    },
    async sendMessage() {
      const message = String(this.draftMessage || '').trim();
      if (!message || this.isSending) {
        return;
      }

      const requestContext = {
        rfc: String(localStorage.getItem('rfc') || '').trim().toUpperCase(),
        period: String(getGlobalPeriod() || '').trim(),
      };

      this.errorMessage = '';
      this.pushMessage('user', message);
      this.draftMessage = '';
      this.isSending = true;

      try {
        const response = await aiService.chat(message, [], requestContext, { skipLoading: true });
        const answer = String(response?.answer || response?.data?.answer || '').trim();

        this.pushMessage('assistant', answer || 'Sin respuesta del servicio.');
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'No fue posible enviar el mensaje de prueba al servicio de chat.';
        this.pushMessage('assistant', 'No pude procesar tu mensaje en este momento.');
      } finally {
        this.isSending = false;
      }
    },
  },
  mounted() {
    this.scrollToBottom();
  },
};
</script>

<style scoped>
.ai-chat-card {
  border: 1px solid var(--sidebar-border);
}

.ai-chat-header {
  background: linear-gradient(95deg, rgba(115, 102, 255, 0.1), rgba(47, 138, 182, 0.08));
}

.ai-chat-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ai-chat-messages {
  min-height: 420px;
  max-height: 58vh;
  overflow-y: auto;
  border: 1px solid var(--sidebar-border);
  border-radius: 10px;
  padding: 14px;
  background: #f8f9fb;
}

.ai-message-row {
  display: flex;
  margin-bottom: 10px;
}

.ai-message-row-user {
  justify-content: flex-end;
}

.ai-message-row-assistant {
  justify-content: flex-start;
}

.ai-message-bubble {
  max-width: min(75%, 720px);
  border-radius: 10px;
  padding: 10px 12px;
  border: 1px solid transparent;
}

.ai-message-bubble-user {
  background: rgba(115, 102, 255, 0.12);
  border-color: rgba(115, 102, 255, 0.25);
}

.ai-message-bubble-assistant {
  background: #ffffff;
  border-color: #dfe3ec;
}

.ai-message-role {
  font-size: 11px;
  font-weight: 700;
  color: #6c757d;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: .3px;
}

.ai-message-text {
  font-size: 14px;
  word-break: break-word;
  color: #212529;
}

.ai-message-text :deep(a) {
  color: #0a66c2;
  text-decoration: underline;
  font-weight: 600;
}

.ai-message-time {
  margin-top: 6px;
  font-size: 11px;
  color: #8a9099;
  text-align: right;
}

.ai-chat-input {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ai-chat-input .form-control {
  resize: none;
}

.ai-chat-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

@media (max-width: 768px) {
  .ai-chat-messages {
    min-height: 360px;
    max-height: 54vh;
  }

  .ai-message-bubble {
    max-width: 92%;
  }
}
</style>
